import PropTypes from 'prop-types';
import React from 'react';
import { jt, t } from 'ttag';
import PriceGenerator from './Price.utils.js';

function Price({
  unitPrice,
  rrpPrice,
  minPrice,
  maxPrice,
  currency,
  vatRate,
  discountPercentage,
  isSoldOut,
  priceSize,
  rrpSize,
  priceColor,
  priceColorDiscount,
  isDeduction,
  display,
  alignment,
  priceFontWeight,
  hasLineThrough,
  extraServicePrice
}) {
  const variantStyling = {
    priceSize: '',
    priceColor: '',
    rrpSize: '',
    display: '',
    alignment: '',
    fontWeight: '',
    priceColorDiscount: ''
  };

  switch (priceColor) {
    case 'black':
      variantStyling.priceColor = 'text-black';
      break;
    case 'green':
      variantStyling.priceColor = 'text-green';
      break;
    case 'dark-gray':
      variantStyling.priceColor = 'text-dark-gray';
      break;
    case 'light-gray':
      variantStyling.priceColor = 'text-gray';
      break;
    case 'orange':
      variantStyling.priceColor = 'text-orange';
      break;
    case 'red':
      variantStyling.priceColor = 'text-cinnebar';
      break;
    case 'white':
      variantStyling.priceColor = 'text-white';
      break;
    default:
      variantStyling.priceColor = 'text-dark-gray';
  }

  switch (priceColorDiscount) {
    case 'black':
      variantStyling.priceColorDiscount = 'text-black';
      break;
    case 'green':
      variantStyling.priceColorDiscount = 'text-green';
      break;
    case 'orange':
      variantStyling.priceColorDiscount = 'text-orange';
      break;
    case 'red':
      variantStyling.priceColorDiscount = 'text-cinnebar';
      break;
    case 'dark-gray':
      variantStyling.priceColorDiscount = 'text-dark-gray';
      break;
    default:
      variantStyling.priceColorDiscount = 'text-dark-gray';
  }

  switch (priceSize) {
    case 'xs':
      variantStyling.priceSize = 'text-xs';
      break;
    case 'sm':
      variantStyling.priceSize = 'text-sm';
      break;
    case 'md':
      variantStyling.priceSize = 'text-base';
      break;
    case 'lg':
      variantStyling.priceSize = 'text-lg';
      break;
    case 'xl':
      variantStyling.priceSize = 'text-xl';
      break;
    case '2xl':
      variantStyling.priceSize = 'text-2xl';
      break;
    case '3xl':
      variantStyling.priceSize = 'text-3xl';
      break;
    default:
      variantStyling.priceSize = 'text-base';
  }

  switch (rrpSize) {
    case 'xs':
      variantStyling.rrpSize = 'text-xs';
      break;
    case 'sm':
      variantStyling.rrpSize = 'text-sm';
      break;
    case 'md':
      variantStyling.rrpSize = 'text-base';
      break;
    case 'lg':
      variantStyling.rrpSize = 'text-lg';
      break;
    case 'xl':
      variantStyling.rrpSize = 'text-xl';
      break;
    case '2xl':
      variantStyling.rrpSize = 'text-2xl';
      break;
    case '3xl':
      variantStyling.rrpSize = 'text-3xl';
      break;
    default:
      variantStyling.rrpSize = 'text-base';
  }

  switch (display) {
    case 'row':
      variantStyling.display = 'flex-row';
      break;
    case 'column':
      variantStyling.display = 'flex-col';
      break;
    default:
      variantStyling.display = 'flex-row';
  }

  switch (alignment) {
    case 'center':
      variantStyling.alignment = 'items-center text-center';
      break;
    case 'left':
      variantStyling.alignment = 'items-start text-left';
      break;
    case 'right':
      variantStyling.alignment = 'items-end text-right';
      break;
    default:
      variantStyling.alignment = 'items-start text-left';
  }

  switch (priceFontWeight) {
    case 'bold':
      variantStyling.fontWeight = 'font-bold';
      break;
    case 'thin':
      variantStyling.fontWeight = 'font-thin';
      break;
    case 'light':
      variantStyling.fontWeight = 'font-light';
      break;
    case 'semibold':
      variantStyling.fontWeight = 'font-semibold';
      break;
    default:
      variantStyling.fontWeight = 'font-bold';
  }

  let unitPriceDecimal = new PriceGenerator(
    minPrice || maxPrice || unitPrice,
    currency
  );
  const discounted = discountPercentage > 0;

  let oldPriceComp;
  const rrpPriceDecimal = new PriceGenerator(rrpPrice, currency);
  if (discounted || (rrpPrice && unitPriceDecimal.lessThan(rrpPriceDecimal))) {
    oldPriceComp = (
      <h3
        className={`m-0 font-thin text-pink-swan line-through ${variantStyling.rrpSize}`}
      >
        {rrpPriceDecimal.toString()}
      </h3>
    );
    variantStyling.priceColor = variantStyling.priceColorDiscount;
  }
  if (extraServicePrice) {
    const extraUnitPrice = unitPriceDecimal.add(extraServicePrice);
    unitPriceDecimal = new PriceGenerator(extraUnitPrice);
  }

  return (
    <div>
      {!isSoldOut ? (
        <div className={`flex flex-col ${variantStyling.alignment}`}>
          <div
            className={`flex ${display === 'row' && 'items-center'} ${
              variantStyling.display
            }`}
          >
            <h3
              className={`m-0 ${hasLineThrough && 'line-through'} ${
                display === 'row' && 'pr-2'
              } ${variantStyling.priceSize}  ${variantStyling.priceColor} ${
                variantStyling.fontWeight
              }`}
            >
              {isDeduction && '- '}
              {minPrice !== maxPrice ? `${t`From`} ` : ''}
              {unitPriceDecimal.toString()}
            </h3>
            {oldPriceComp}
          </div>
          {vatRate > 0 && (
            <p className="m-0 text-base font-normal text-pink-swan">{jt`Price is incl. ${vatRate}% VAT`}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

Price.propTypes = {
  unitPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  rrpPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currency: PropTypes.oneOf(['DKK', 'EUR', 'NOK', 'SEK', '']),
  vatRate: PropTypes.number,
  discountPercentage: PropTypes.number,
  isSoldOut: PropTypes.bool,
  minPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extraServicePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  priceSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  rrpSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  isDeduction: PropTypes.bool,
  priceColor: PropTypes.oneOf([
    'green',
    'black',
    'dark-gray',
    'light-gray',
    'orange',
    'red',
    'white'
  ]),
  priceColorDiscount: PropTypes.oneOf([
    'green',
    'dark-gray',
    'black',
    'orange',
    'red'
  ]),
  display: PropTypes.oneOf(['row', 'column']),
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  priceFontWeight: PropTypes.oneOf(['thin', 'light', 'semibold', 'bold']),
  hasLineThrough: PropTypes.bool
};

Price.defaultProps = {
  rrpPrice: 0,
  currency: '',
  vatRate: 0,
  discountPercentage: 0,
  isSoldOut: false,
  minPrice: 0,
  maxPrice: 0,
  extraServicePrice: 0,
  priceSize: 'md',
  rrpSize: 'md',
  isDeduction: false,
  priceColor: 'dark-gray',
  priceColorDiscount: 'dark-gray',
  display: 'row',
  alignment: 'left',
  priceFontWeight: 'bold',
  hasLineThrough: false
};

export default Price;
